import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom"; // <-- NEW
import { BrowserRouter as Router, Routes, Route, useParams } from "react-router-dom";
import Header from "./Header";
import { FlipBook } from "./FlipBook";
import { BottomBar } from "./BottomBar";
import { LogoLoader } from "./logo-loader";
import "./index.css";

const ZOOM_LEVELS = [100, 125, 150, 200];

// Catalog page just tries to load the remote PDF
function CatalogPage({ onReady }) {
  const { catalogName } = useParams();

  // Default PDF for local testing
  const DEFAULT_PDF = "/example.pdf";

  // If catalogName exists, use remote PDF; otherwise, use default
  const pdfUrl = catalogName
    ? `https://files.atlasimex.es/catalogs/${catalogName}.pdf`
    : DEFAULT_PDF;

  // Use a public test PDF to test it
  /*const pdfUrl = catalogName
    ? "https://raw.githubusercontent.com/mozilla/pdf.js/master/web/compressed.tracemonkey-pldi-09.pdf"
    : DEFAULT_PDF;  */ 

  const flipbookRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hideHeader, setHideHeader] = useState(false);
  const [pdfNotFound, setPdfNotFound] = useState(false);

  // NEW: zoom state (CSS classes exist in index.css)
  const [zoomLevel, setZoomLevel] = useState(100);

  const increaseZoom = useCallback(
    () =>
      setZoomLevel((z) => {
        const idx = ZOOM_LEVELS.indexOf(z);
        return ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, idx + 1)] ?? z;
      }),
    []
  );

  const decreaseZoom = useCallback(
    () =>
      setZoomLevel((z) => {
        const idx = ZOOM_LEVELS.indexOf(z);
        return ZOOM_LEVELS[Math.max(0, idx - 1)] ?? z;
      }),
    []
  );

  const handlePinch = useCallback(
    (scale) => {
      if (scale > 1) increaseZoom();
      else if (scale < 1) decreaseZoom();
    },
    [increaseZoom, decreaseZoom]
  );

  const enterFullscreen = async () => {
    // try flipbook container first, fallback to document body
    const el =
      flipbookRef.current?.container ||
      document.querySelector(".flipbook-container") ||
      document.querySelector(".app-main") ||
      document.documentElement;
    if (!el) return;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) await el.msRequestFullscreen();
    } catch (e) {
      /* ignore */
    }
  };

  // Fullscreen state + handler to exit fullscreen
  const [isFullscreenLocal, setIsFullscreenLocal] = useState(Boolean(document.fullscreenElement));
  useEffect(() => {
    const onFsChange = () => setIsFullscreenLocal(Boolean(document.fullscreenElement));
    window.addEventListener("fullscreenchange", onFsChange);
    window.addEventListener("webkitfullscreenchange", onFsChange);
    window.addEventListener("msfullscreenchange", onFsChange);
    return () => {
      window.removeEventListener("fullscreenchange", onFsChange);
      window.removeEventListener("webkitfullscreenchange", onFsChange);
      window.removeEventListener("msfullscreenchange", onFsChange);
    };
  }, []);

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      else if (document.msExitFullscreen) await document.msExitFullscreen();
    } catch (e) {
      /* ignore */
    }
  };

  // Keep app-root class in sync so CSS zoom-* rules take effect
  useEffect(() => {
    const root = document.querySelector(".app-root");
    if (!root) return;
    root.classList.remove("zoom-100", "zoom-125", "zoom-150", "zoom-200");
    root.classList.add(`zoom-${zoomLevel}`);
  }, [zoomLevel]);

  // prepare exit button once so we can portal it
  const exitButton = (
    <button
      className="exit-fullscreen-btn"
      onClick={exitFullscreen}
      aria-label="Exit"
    >
      <span className="exit-icon" aria-hidden>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="exit-label">Exit</span>
    </button>
  );

  // Use catalog name as-is for the downloaded filename
  const downloadName = `${catalogName || "catalog"}.pdf`;

  const handleDownload = async (e) => {
    e?.preventDefault();
    try {
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(pdfUrl, "_blank");
    }
  };

  const formatTitle = (title) => {
    if (title === undefined || title === null) return "Catalog";
    if (title === "") return "";
    return title
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map((word) =>
        word.length <= 3
          ? word.toUpperCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  };

  useEffect(() => {
    const nextTitle = pdfNotFound
      ? "Catalog not found"
      : formatTitle(catalogName);
    document.title = nextTitle || "Catalog";
  }, [catalogName, pdfNotFound]);

  const handlePageChange = useCallback((p) => setCurrentPage(p), []);
  const handleHideHeaderChange = useCallback((v) => setHideHeader(Boolean(v)), []);
  const readyRef = useRef(false);

  const signalReadyOnce = useCallback(() => {
    if (readyRef.current) return;
    readyRef.current = true;
    onReady?.();
  }, [onReady]);

  const handlePdfError = useCallback((isError) => {
    setPdfNotFound(isError);
    if (isError) signalReadyOnce();
  }, [signalReadyOnce]);

  const handleTotalPagesChange = useCallback((n) => setTotalPages(n), []);

  return (
    <div className={`app-root zoom-${zoomLevel}`}>
      {!hideHeader && (
        <Header
          onDownload={handleDownload}
          flipbookRef={flipbookRef}
          downloadUrl={pdfUrl}
          downloadName={downloadName}
        />
      )}

      <main className="app-main">
        {pdfNotFound ? (
          <main className="notfound">
            <div className="notfound__content">
              <h1 className="notfound__title">Catalog not found</h1>
              <p className="notfound__text">
                Sorry, we couldn’t find the catalog you’re looking for.
              </p>
            </div>
          </main>
        ) : (
          <FlipBook
            ref={flipbookRef}
            pdfUrl={pdfUrl}
            onPageChange={handlePageChange}
            onHideHeaderChange={handleHideHeaderChange}
            onPinch={handlePinch}
            onPdfError={handlePdfError}
            onTotalPagesChange={handleTotalPagesChange}
            onPdfReady={signalReadyOnce}
          />
        )}
      </main>

      {!pdfNotFound && (
        <BottomBar
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => flipbookRef.current?.goToPage(p - 1)}
          // NEW: pass zoom and fullscreen handlers
          onZoomIn={increaseZoom}
          onZoomOut={decreaseZoom}
          onFullscreen={enterFullscreen}
          isFullscreen={Boolean(document.fullscreenElement)}
        />
      )}

     {/* Render exit button inside the fullscreen element using a portal so it's visible when flipbook is fullscreen */}
     {isFullscreenLocal &&
        (typeof document !== "undefined" && document.fullscreenElement
          ? createPortal(exitButton, document.fullscreenElement)
          : exitButton)}
    </div>
  );
}


// Root App with router
export default function App() {
  // show loader until window load completes
  const [loading, setLoading] = useState(() => document.readyState !== "complete");
  const [progress, setProgress] = useState(0);
  const [isAppReady, setIsAppReady] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);

  const handlePdfReady = useCallback(() => setPdfLoaded(true), []);

  useEffect(() => {
    if (document.readyState === "complete") {
      setLoading(false);
      return;
    }
    const onLoad = () => setTimeout(() => setLoading(false), 200);
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  useEffect(() => {
    let timer;
    if (pdfLoaded) {
      setProgress(100);
      timer = setTimeout(() => setIsAppReady(true), 800); // Give it a moment to breathe
    } else {
      timer = setInterval(() => {
        setProgress((old) => (old >= 95 ? 95 : old + 2));
      }, 100);
    }
    return () => {
      clearInterval(timer);
      clearTimeout(timer);
    };
  }, [pdfLoaded]);

  const loaderPhase = loading
    ? "Powering up..."
    : pdfLoaded
      ? "Starting now!"
      : "Loading the catalog...";

  return (
    <Router>
      {(loading || !isAppReady) && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
          <LogoLoader progress={progress} phase={loaderPhase} />
        </div>
      )}

      <div
        className="app-container"
        style={{
          visibility: isAppReady ? "visible" : "hidden",
          opacity: isAppReady ? 1 : 0,
          transition: "opacity 0.5s ease-in-out"
        }}
      >
        <Routes>
          <Route path="/" element={<CatalogPage onReady={handlePdfReady} />} />
          <Route path="/:catalogName" element={<CatalogPage onReady={handlePdfReady} />} />
          <Route path="*" element={<div>Catalog not found</div>} />
        </Routes>
      </div>
    </Router>
  );
}
